import { faEnvelope, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StaticImage } from "gatsby-plugin-image";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import tw, { css } from "twin.macro";

import Roll20Logo from "../../assets/images/customers/roll20-white.svg";
import BackgroundImage from "../../assets/images/demo-background.png";
import Logo, { LogoColor } from "../layout/partials/Logo";
import Button from "./Button";
import Modal from "./Modal";
import ScheduleMeetingModal from "./ScheduleMeetingModal";

const DemoModal: React.FunctionComponent = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [scheduleMeetingModalIsOpen, setScheduleMeetingModalIsOpen] =
    useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formLoadError, setFormLoadError] = useState(false);
  const [clicked, setClicked] = useState<boolean>(false);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains("get-a-demo")) {
        e.preventDefault();
        setClicked(true);
        setIsOpen(true);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    if (window.location.hash === "#get-a-demo") {
      setIsOpen(true);
    }
  }, []);

  const showScheduleModal = () => {
    if (!(window as any).hbspt) {
      setFormLoadError(true);
      return;
    }

    setScheduleMeetingModalIsOpen(true);
  };

  const showReachOutForm = () => {
    if (!(window as any).hbspt) {
      setFormLoadError(true);
      return;
    }

    setFormLoading(true);

    (window as any).hbspt.forms.create({
      region: "na1",
      portalId: "7620271",
      formId: "ea393924-aa79-412b-9615-f5d0dc92e966",
      target: "#request-demo-form",
    });
  };

  useEffect(() => {
    if (showForm === false) {
      const iframe = document.querySelector("#request-demo-form iframe");
      iframe?.parentNode?.removeChild(iframe);
    }
  }, [showForm]);

  const handleHubSpotFormReady = (e: MessageEvent) => {
    if (
      e.data.type === "hsFormCallback" &&
      e.data.eventName === "onFormReady"
    ) {
      window.setTimeout(() => {
        setFormLoading(false);
        setShowForm(true);
      }, 200);
    }
  };

  useEffect(() => {
    if (!clicked) {
      return;
    }
    window.addEventListener("message", handleHubSpotFormReady);

    return () => {
      window.removeEventListener("message", handleHubSpotFormReady);
    };
  }, [clicked]);

  return (
    <>
      {clicked && (
        <Helmet>
          <script async src="https://js.hsforms.net/forms/embed/v2.js"></script>
        </Helmet>
      )}
      <Modal
        show={isOpen}
        id="demo-modal"
        css={css`
          font-size: 16px;
          line-height: 24px;
          .modal-content {
            padding: 0;
          }
          .modal-inner {
            max-width: 1070px;
            border-radius: 10px;
          }
        `}
        handleClose={() => {
          setShowForm(false);
          setIsOpen(false);
        }}
      >
        <div
          tw="flex"
          css={css`
            @media (max-width: 890px) {
              flex-direction: column-reverse;
            }
          `}
        >
          <div
            tw="flex flex-col"
            css={css`
              background-color: #1c63d9;
              background-image: url(${BackgroundImage});
              background-size: 875px 553px;
              background-repeat: no-repeat;
              color: #fff;
              padding: 1rem;
              min-width: 475px;
              min-height: 518px;

              @media (max-width: 985px) {
                min-width: 420px;
              }

              @media (max-width: 890px) {
                min-height: 375px;
                min-width: unset;
              }
            `}
          >
            <div>
              <Logo color={LogoColor.White} width={126} />
            </div>

            <div
              tw="flex-1 flex flex-col justify-center"
              css={css`
                max-width: 327px;
                margin: 0 auto;
              `}
            >
              <div>
                “ProsperStack has reduced overall churn by about 5%, which is a
                significant number for us. Onboarding was super easy — a few
                lines of code and it was integrated.”
              </div>
              <div tw="mt-4">
                <img
                  src={Roll20Logo}
                  alt="Roll20"
                  css={css`
                    width: 113px;
                    margin: 0 auto;
                  `}
                />
              </div>
              <div tw="mt-6 flex">
                <div>
                  <div
                    css={css`
                      width: 49px;
                      height: 49px;
                      border-radius: 50%;
                      overflow: hidden;
                    `}
                  >
                    <StaticImage
                      src="../../assets/images/customers/clay-small.png"
                      alt=""
                      width={49}
                      height={49}
                      placeholder="none"
                    />
                  </div>
                </div>
                <div
                  css={css`
                    font-size: 14px;
                    margin-left: 10px;
                  `}
                >
                  <div tw="font-title">Clay Reimus</div>
                  <div>Director of Growth &amp; Analytics</div>
                </div>
              </div>
            </div>
            <div
              tw="mt-auto text-center"
              css={css`
                a:not(.button) {
                  color: white;
                  font-weight: normal;
                }
              `}
            >
              <div tw="font-title">Contact sales:</div>
              <div tw="hidden">
                <FontAwesomeIcon icon={faPhone} />{" "}
                <a href="tel:+16125551234">+1 (612) 555-2134</a>
              </div>
              <div>
                <FontAwesomeIcon icon={faEnvelope} transform="down-1" />{" "}
                <a href="mailto:sales@prosperstack.com">
                  sales@prosperstack.com
                </a>
              </div>
            </div>
          </div>
          <div tw="bg-white flex-1 p-4 py-8">
            <div
              css={
                showForm
                  ? tw`flex flex-col justify-center h-full px-4`
                  : undefined
              }
            >
              {showForm && <h4>Request a demo</h4>}

              <div
                id="request-demo-form"
                css={
                  !showForm
                    ? css`
                        display: none;
                      `
                    : undefined
                }
              />
            </div>

            {!showForm && (
              <div tw="flex flex-col items-center justify-center h-full">
                <div
                  css={css`
                    max-width: 469px;
                    margin: 0 auto;

                    @media (max-width: 890px) {
                      padding-top: 20px;
                    }
                    @media (max-width: 600px) {
                      max-width: 369px;
                    }
                  `}
                >
                  <p>
                    <strong>Meet ProsperStack</strong>: The only platform that
                    automates and enhances subscriber retention experiences so
                    you can keep more of your most valuable customers.
                  </p>
                  <p>
                    Learn how by booking a demo with one of our retention
                    experts today.
                  </p>
                </div>
                <div
                  tw="flex"
                  css={css`
                    max-width: 469px;
                    width: 100%;
                    margin: 0 auto;
                    button {
                      text-transform: none;
                      flex: 1;
                    }
                    @media (max-width: 1000px) {
                      flex-direction: column;
                      button {
                        width: 100%;
                        margin-bottom: 0.5rem;
                      }
                    }
                    @media (max-width: 890px) {
                      flex-direction: row;
                      button {
                        width: auto;
                        margin-bottom: 0.5rem;
                      }
                    }
                    @media (max-width: 600px) {
                      max-width: 369px;
                      flex-direction: column;

                      button {
                        width: 100%;
                        margin-bottom: 0.5rem;
                      }
                    }
                  `}
                >
                  <Button
                    color="primary"
                    tw="mr-2"
                    onClick={() => showScheduleModal()}
                  >
                    Schedule a demo
                  </Button>
                  <Button
                    color="white"
                    onClick={() => showReachOutForm()}
                    disabled={formLoading}
                    loading={formLoading}
                  >
                    Reach out to me
                  </Button>
                </div>
                {formLoadError && (
                  <div tw="mt-5 max-w-[469px] mx-auto text-red-400">
                    Oops, there was an error loading the form. This could be due
                    to a browser plugin or privacy setting. You can always
                    contact us via email:{" "}
                    <a href="mailto:sales@prosperstack.com">
                      sales@prosperstack.com
                    </a>
                    .
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
      <ScheduleMeetingModal
        isOpen={scheduleMeetingModalIsOpen}
        onClose={() => {
          setShowForm(false);
          setScheduleMeetingModalIsOpen(false);
        }}
      />
    </>
  );
};

export default DemoModal;
